import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";

import Modal from "../Modal";
import ButtonBubble from "../ButtonBubble";
import ButtonPrimary from "../ButtonPrimary";
import Selector from "../Selector";
import SubscribersSection from "../SubscribersSection";

import CampaignMembersStore from "./CampaignMembers.store";
import withRefluxStore from "../../Stores/withRefluxStore.jsx";

import useModal from "./hooks/useModal";
import useCampaignMembers from "./hooks/useCampaignMembers";
import useUpdateCampaignMembers from "./hooks/useUpdateCampaignMembers";

import {
  InviteMembersEmbeddedContent,
  ThemeProvider,
  theme,
  Store__System
} from "@talentbait/shared-components";
import { CAMPAIGN_ATS, ROLE_OWNER, ROLE_RECRUITER } from "./CampaignMembers.constants";
import * as Styled from "./CampaignMembers.styled";

export const CampaignMembers = ({
  campaignId,
  campaignMembers,
  campaignGoal,
  isFetchingCampaignMembers,
  isUpdatingCampaignMembers,
  hasCollectiveInbox,
  organization,
  user
}) => {
  const { t } = useTranslation();
  const { isModalOpen, handleOpenModal, handleCloseModal } = useModal(campaignId);
  const { departmentScout = false } = useFlags();

  const isLoading = isUpdatingCampaignMembers || isFetchingCampaignMembers;
  const [changesHaveBeenMade, setChangesHaveBeenMade] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [typeOfSubscriber, setTypeOfSubscriber] = useState(null);

  const {
    onChangeOwner,
    onChangeRecruiter,
    onChangeSubscribersAts,
    onChangeSubscribersCampaign,
    ownerId,
    recruiterId,
    subscribersAtsIds,
    subscribersCampaignIds,
    addedSubscribersAts,
    addedSubscribersCampaign
  } = useCampaignMembers({
    campaignId,
    campaignMembers,
    setChangesHaveBeenMade
  });

  const { handleSubmit } = useUpdateCampaignMembers({
    campaignId,
    campaignMembers,
    handleCloseModal,
    ownerId,
    recruiterId,
    setChangesHaveBeenMade,
    subscribersAtsIds,
    subscribersCampaignIds,
    addedSubscribersAts,
    addedSubscribersCampaign
  });

  const onAddNewOption = (typeOfSubscriber) => {
    setTypeOfSubscriber(typeOfSubscriber);
    setShowInvite(true);
  };

  let members = [];
  if (organization) {
    Object.keys(organization.members).forEach((key) => {
      const member = organization.members[key];

      if (!member.id) {
        member.id = key;
      }

      members.push(member);
    });
  }

  return (
    <ThemeProvider theme={theme}>
      <ButtonBubble
        bubbleId="campaignMembersBubble"
        isLoading={isLoading}
        members={campaignMembers}
        onClick={handleOpenModal}
      />

      <Modal
        headline={t("Settings - Campaign members")}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      >
        {showInvite && (
          <Styled.InviteBubbleWrapper>
            <Styled.InviteContent>
              <InviteMembersEmbeddedContent
                hasDepartmentScoutFlag={departmentScout}
                organization={organization}
                user={user}
                source={"Campaign Members"}
                resetShowModal={() => setShowInvite(false)}
                organizationId={organization?.id}
                allMembers={members}
                typeOfSubscriber={typeOfSubscriber}
                campaignId={campaignId}
              />
            </Styled.InviteContent>
          </Styled.InviteBubbleWrapper>
        )}
        <div>
          <Styled.DropdownsWrapper>
            <Selector
              description={t(
                "Define who will receive notifications in exchange with the Campaign Managers."
              )}
              members={campaignMembers}
              onChange={onChangeOwner}
              role={ROLE_OWNER}
              title={t("Campaign owner")}
            />

            {campaignGoal === CAMPAIGN_ATS && (
              <Selector
                description={t(
                  "Define a main person responsible for the applicants, as well as the sender of e-mails and WhatsApp as well as SMS communication."
                )}
                members={campaignMembers}
                onChange={onChangeRecruiter}
                role={ROLE_RECRUITER}
                title={t("Campaign recruiter")}
              />
            )}
          </Styled.DropdownsWrapper>

          <SubscribersSection
            members={campaignMembers}
            onChangeSubscribersAts={onChangeSubscribersAts}
            onChangeSubscribersCampaign={onChangeSubscribersCampaign}
            onAddNewOption={(subscriberType) => onAddNewOption(subscriberType)}
            showSubscribersAts={campaignGoal === CAMPAIGN_ATS}
            isDisabled={hasCollectiveInbox}
            userEmail={user?.email}
          />

          <Styled.Footer>
            <Styled.Text>
              {t("Not yet on the list?")}{" "}
              <Styled.Link href={`/settings/team-members`}>{t("Add a new user.")}</Styled.Link>
            </Styled.Text>

            <ButtonPrimary
              onClick={handleSubmit}
              isLoading={isLoading}
              disabled={!changesHaveBeenMade}
            >
              {t("Save")}
            </ButtonPrimary>
          </Styled.Footer>
        </div>
      </Modal>
    </ThemeProvider>
  );
};

export default withRefluxStore(
  CampaignMembers,
  [CampaignMembersStore, Store__System],
  ["organization", "campaignMembers", "user"]
);
